import React, { useState, useRef, useEffect } from 'react';

interface SimpleSelectProps {
  value: string | number;
  options: (string | number)[];
  onChange: (value: string | number) => void;
  className?: string;
}

const SimpleSelect: React.FC<SimpleSelectProps> = ({
  value,
  options,
  onChange,
  className = 'w-48',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // 🔒 Cierra al hacer click fuera del componente
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      {/* 📦 Display seleccionado */}
      <div
        className="flex cursor-pointer items-center justify-between rounded border border-stroke bg-white px-3 py-2 dark:border-strokedark dark:bg-boxdark"
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <span className="text-gray-800 dark:text-gray-200 text-sm">
          {value}
        </span>

        {/* ⬇️ Flecha animada */}
        <span
          role="button"
          tabIndex={0}
          onClick={(e) => {
            e.stopPropagation();
            setIsOpen((prev) => !prev);
          }}
          className="ml-2 flex items-center justify-center transition hover:opacity-80"
        >
          <svg
            className={`h-4 w-4 transform transition-transform duration-300 ease-in-out ${
              isOpen
                ? 'rotate-180 text-primary'
                : 'rotate-0 text-gray-500 dark:text-white'
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </span>
      </div>

      {/* 🧩 Opciones (en grid) */}
      {isOpen && (
        <div className="absolute left-0 top-full z-10 mt-2 w-full rounded-lg border border-stroke bg-white p-3 shadow-lg dark:border-strokedark dark:bg-boxdark">
          <div
            className="grid gap-3"
            style={{
              gridTemplateColumns: 'repeat(auto-fit, minmax(2rem, 1fr))',
            }}
          >
            {options.map((opt) => (
              <div
                key={opt}
                className={`mx-auto flex h-8 w-8 cursor-pointer select-none items-center justify-center rounded-md text-xs font-medium transition-all duration-200
                ${
                  String(opt) === String(value)
                    ? 'scale-105 bg-primary text-white shadow-md'
                    : 'bg-gray-100 text-gray-800 dark:text-gray-200 hover:scale-105 hover:bg-primary hover:text-white hover:shadow-md dark:bg-slate-700'
                }`}
                onClick={() => {
                  onChange(opt);
                  setIsOpen(false);
                }}
              >
                {opt}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SimpleSelect;
